/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Adobe Analytics data layer (UCR-2938, Phase 1). This is the Google-style `window.dataLayer` array
 * that Adobe Tags (Launch) consumes: each push carries an `event` string (which maps to a Direct Call
 * Rule via the Analytics team's bridge) plus the app/catalog/CMS context that only the storefront can
 * supply. Everything derivable client-side (URL, campaign params, ECID, scroll, etc.) is handled in
 * Launch and intentionally NOT pushed here.
 *
 * Distinct from `window.adobeDataLayer` (the ACDL `page-view` tracker in `adobe-data-layer.tsx`) — that
 * layer is left as-is; this is the SDR-mapped layer the Analytics team specified.
 */

/** The brand's analytics "site" id — a constant on every push (SDR v57/p17). Matches `chat.brandName`. */
export const DATA_LAYER_SITE = 'blackmagic';

/** Allowed `page.pageType` values, from the site structure (spec). `unknown` is the safe default. */
export type DataLayerPageType =
    | 'home'
    | 'category'
    | 'product'
    | 'article'
    | 'content'
    | 'about'
    | 'faq'
    | 'contact'
    | 'search-results'
    | 'unknown';

/** Required on every page. `pageName` follows `site:section:subsection` (see {@link buildPageName}). */
export interface DataLayerPage {
    pageName: string;
    pageType: DataLayerPageType;
}

/** PLP context (SDR v53/v54/v58). `filtersApplied` is the merchandiser-facing filter path, or omitted. */
export interface DataLayerListing {
    category: string;
    filtersApplied?: string;
}

/** PDP context (SDR v43/v10/v21…). `price` is a string ("12.97"); `rating` may be empty. */
export interface DataLayerProduct {
    sku: string;
    name?: string;
    brand?: string;
    price?: string;
    rating?: string;
}

/** Search context. `resultCount` is numeric — 0, never the string "zero" (spec/SDR v3). */
export interface DataLayerSearch {
    term: string;
    resultCount: number;
}

/** Article/CMS context (SDR v22/v36/v32). */
export interface DataLayerArticle {
    id?: string;
    name?: string;
    dates?: string;
}

/**
 * The analytics payload a route loader returns (read by `DataLayerTracker` via `useMatches()`), so the
 * router — which owns page identity — supplies a stable `pageName`/`pageType` and the page's context.
 * `pageEvent` is an extra event pushed right after `pageView` (e.g. `productView`, `articleView`).
 */
export interface RouteAnalytics {
    page: DataLayerPage;
    listing?: DataLayerListing;
    product?: DataLayerProduct;
    search?: DataLayerSearch;
    article?: DataLayerArticle;
    pageEvent?: string;
}

declare global {
    interface Window {
        /** Adobe Analytics data layer consumed by Adobe Tags (Launch). Bootstrapped in `root.tsx` <head>. */
        dataLayer?: Array<Record<string, unknown>>;
    }
}

/**
 * Push an event to `window.dataLayer`, always stamping the constant `site`. No-op during SSR. Creates
 * the array defensively if the `<head>` bootstrap didn't run, so a push is never lost.
 */
export function pushDataLayer(event: string, data: Record<string, unknown> = {}): void {
    if (typeof window === 'undefined') return;
    const dataLayer = (window.dataLayer = window.dataLayer ?? []);
    dataLayer.push({ event, site: DATA_LAYER_SITE, ...data });
}

/**
 * Build a `site:section:subsection` page name — e.g. `buildPageName('shop', 'hydroponics')` →
 * `"blackmagic:shop:hydroponics"`. Blank segments are dropped; the `site` prefix is always first.
 */
export function buildPageName(...segments: Array<string | undefined | null>): string {
    const parts = segments.map((s) => s?.trim()).filter((s): s is string => Boolean(s));
    return [DATA_LAYER_SITE, ...parts].join(':');
}

/**
 * Push a `pageView` for a route's analytics payload (page + any context objects), then the optional
 * page-type event (`productView`/`articleView`). Called by `DataLayerTracker` on each navigation.
 */
export function pushPageView(analytics: RouteAnalytics): void {
    const { page, listing, product, search, article, pageEvent } = analytics;
    pushDataLayer('pageView', {
        page,
        ...(listing && { listing }),
        ...(product && { product }),
        ...(search && { search }),
        ...(article && { article }),
    });
    if (pageEvent) pushDataLayer(pageEvent);
}
